import { useEffect, useRef } from 'react';
import { useRouter } from 'next/compat/router';

import { useAccountSignupAnalytics, useIsAccountSignupAnalyticsEnabled } from 'src/hooks/use-account-signup-analytics';
import { normalizeIsNewUser, SOCIAL_AUTH_SOURCE_SIGNUP } from 'src/oauth/append-is-new-user';
import type { AccountSignupSurface } from 'src/utils/analytics/events';

export type SocialOAuthProvider = 'apple' | 'google';

export type UseSocialOAuthSignupAnalyticsOptions = {
  provider: SocialOAuthProvider;
};

// Query param a signup surface stamps on its social redirectUrl (only while the rollout flag is
// on) so the OAuth callback can attribute a backend-confirmed net-new account to that surface.
// Deliberately distinct from socialAuthSource=signup, which triggers the post-signup onboarding
// modal sequence and must not fire on kiosk/enterprise flows.
export const SIGNUP_ANALYTICS_SURFACE_PARAM = 'signupAnalyticsSurface';

export type BuildSignupMarkedRedirectUrlOptions = {
  href: string;
  surface: AccountSignupSurface;
  isEnabled: boolean;
};

// Stamps the surface marker the OAuth callback uses to attribute a backend-confirmed net-new
// account. Marking stays flag-gated so the marker can never outlive the rollout.
export const buildSignupMarkedRedirectUrl = ({
  href,
  surface,
  isEnabled,
}: BuildSignupMarkedRedirectUrlOptions): string => {
  const url = new URL(href);
  if (isEnabled) {
    url.searchParams.set(SIGNUP_ANALYTICS_SURFACE_PARAM, surface);
  }
  return url.toString();
};

// Each marked surface must round-trip to a route that actually belongs to it, so a crafted
// redirectUrl can't misattribute a marketplace signup to another surface.
const SURFACE_PATH_PREFIXES: Partial<Record<AccountSignupSurface, string[]>> = {
  instoreEnrollment: ['/instore-enrollment/'],
  enterpriseSignup: ['/enterprise/', '/stores/enterprise/'],
};

const isSurfaceMarkedRedirect = (url: URL, surface: AccountSignupSurface): boolean =>
  (SURFACE_PATH_PREFIXES[surface] ?? []).some((prefix) => url.pathname.startsWith(prefix));

// Resolves the signup surface that marked this redirectUrl, or null when the redirect isn't
// signup-attributable. socialAuthSource=signup maps to the signup modal's socialOAuth surface;
// signupAnalyticsSurface marks instore/enterprise and must also pass the pathname check.
const parseSignupAnalyticsSurface = (redirectUrl: string[] | string | undefined): AccountSignupSurface | null => {
  if (redirectUrl === undefined || Array.isArray(redirectUrl) || redirectUrl === '') {
    return null;
  }

  try {
    const url = new URL(redirectUrl, window.location.origin);

    if (url.searchParams.get('socialAuthSource') === SOCIAL_AUTH_SOURCE_SIGNUP) {
      return 'socialOAuth';
    }

    const markedSurface = url.searchParams.get(SIGNUP_ANALYTICS_SURFACE_PARAM) as AccountSignupSurface | null;
    if (markedSurface !== null && isSurfaceMarkedRedirect(url, markedSurface)) {
      return markedSurface;
    }

    return null;
  } catch {
    return null;
  }
};

// Emits the terminal accountSignupAction event for a social OAuth callback once the callback has
// resolved. Only fires when the redirect URL was marked by a signup surface; returning-user
// logins (isNewUser=false/absent) are excluded by emitting nothing.
export const useSocialOAuthSignupAnalytics = ({ provider }: UseSocialOAuthSignupAnalyticsOptions): void => {
  const router = useRouter();
  const { isReady, query } = router ?? {};
  const { token, redirectUrl, isNewUser } = query ?? {};
  const isEnabled = useIsAccountSignupAnalyticsEnabled();
  const signupSurface = parseSignupAnalyticsSurface(redirectUrl);
  const { trackSucceeded, trackFailed } = useAccountSignupAnalytics({ surface: signupSurface, method: provider });
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    // A false flag reading may just mean flags haven't streamed in yet, so latch the replay guard
    // only once tracking is live. The guard then dedupes effect replays from User.updateToken and
    // the shallow redirect replace so one callback resolution emits at most one terminal event.
    if (!isReady || !isEnabled || hasTrackedRef.current || signupSurface === null) {
      return;
    }

    hasTrackedRef.current = true;

    if (!token) {
      trackFailed({ errorCode: 'oauth_callback_no_token' });
      return;
    }

    // isNewUser is the provider callback's server-side account-creation fact; only 'true' is a
    // confirmed net-new account.
    if (normalizeIsNewUser(isNewUser) === 'true') {
      trackSucceeded();
    }
  }, [isReady, isEnabled, token, signupSurface, isNewUser, trackSucceeded, trackFailed]);
};
